import { Button } from '@/components/ui/button'
import { Camera, Heart, Needle, PersonSimpleRun } from '@phosphor-icons/react'
import { Link } from 'react-router-dom'
import { type Language } from './LandingPage'

const content = {
  pt: {
    title: 'Casos de uso em que a data é o produto.',
    subtitle: 'Sem exagero comercial: cada nicho usa o PinDay para reservar datas com cobrança e regras claras.',
    cta: 'Criar conta',
    items: [
      { icon: Camera, title: 'Fotógrafos', problem: 'Perder sábados esperando “confirmo depois”.', solution: 'Use one-off com sinal, política de cancelamento e link de saldo antes do evento.' },
      { icon: PersonSimpleRun, title: 'Doulas', problem: 'Precisa reservar janela e alinhar expectativas sem depender de horário.', solution: 'Estruture a reserva por data com regras claras, hold e comunicação do fluxo.' },
      { icon: Needle, title: 'Tatuadores', problem: 'No-show de sessão longa custa o dia todo.', solution: 'Trave a data com cobrança one-off, sinal e taxa de reagendamento quando necessário.' },
      { icon: Heart, title: 'Wedding planners', problem: 'Operações mistas exigem reserva por data e cobrança recorrente em alguns contratos.', solution: 'Use one-off para reservas pontuais e recorrente via Stripe quando a operação pedir mensalidade.' },
    ],
  },
  en: {
    title: 'Use cases where the date is the product.',
    subtitle: 'No inflated promises: each niche uses PinDay to reserve dates with payment and clear rules.',
    cta: 'Create account',
    items: [
      { icon: Camera, title: 'Photographers', problem: 'Losing Saturdays while waiting for “I will confirm later.”', solution: 'Use one-off deposits, cancellation rules, and balance links before the event.' },
      { icon: PersonSimpleRun, title: 'Doulas', problem: 'You need to reserve a window and align expectations without relying on time slots.', solution: 'Structure the booking by date with clear rules, holds, and communication.' },
      { icon: Needle, title: 'Tattoo artists', problem: 'A long-session no-show burns the whole day.', solution: 'Lock the date with one-off billing, deposit, and reschedule fees when needed.' },
      { icon: Heart, title: 'Wedding planners', problem: 'Mixed operations need date reservations and recurring billing in some contracts.', solution: 'Use one-off for date bookings and recurring Stripe billing when the operation needs a subscription.' },
    ],
  },
  es: {
    title: 'Casos de uso donde la fecha es el producto.',
    subtitle: 'Sin exageraciones comerciales: cada nicho usa PinDay para reservar fechas con pago y reglas claras.',
    cta: 'Crear cuenta',
    items: [
      { icon: Camera, title: 'Fotógrafos', problem: 'Perder sábados esperando “te confirmo después”.', solution: 'Usa one-off con seña, política de cancelación y link de saldo antes del evento.' },
      { icon: PersonSimpleRun, title: 'Doulas', problem: 'Necesitas reservar una ventana y alinear expectativas sin depender de horarios.', solution: 'Estructura la reserva por fecha con reglas claras, hold y comunicación del flujo.' },
      { icon: Needle, title: 'Tatuadores', problem: 'Un no-show en sesión larga cuesta el día entero.', solution: 'Bloquea la fecha con cobro one-off, seña y tasa de reprogramación cuando haga falta.' },
      { icon: Heart, title: 'Wedding planners', problem: 'Operaciones mixtas necesitan reserva por fecha y cobro recurrente en algunos contratos.', solution: 'Usa one-off para reservas puntuales y recurrente vía Stripe cuando la operación necesite mensualidad.' },
    ],
  },
}

interface ICPSectionProps {
  language: Language
}

export function ICPSection({ language }: ICPSectionProps) {
  const t = content[language]

  return (
    <section className="px-6 py-20">
      <div className="mx-auto max-w-7xl">
        <div className="max-w-3xl">
          <h2 className="text-4xl font-semibold tracking-tight text-[#0b2a4a] sm:text-5xl">{t.title}</h2>
          <p className="mt-4 text-lg text-[#0b2a4a]/72">{t.subtitle}</p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {t.items.map((item) => {
            const Icon = item.icon
            return (
              <article key={item.title} className="rounded-[30px] border border-[#0b2a4a]/10 bg-white p-8 shadow-[0_18px_56px_rgba(11,42,74,0.06)]">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f08a24]/12 text-[#f08a24]">
                    <Icon size={28} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#0b2a4a]">{item.title}</h3>
                </div>
                <p className="mt-6 text-base leading-7 text-[#0b2a4a]/68">{item.problem}</p>
                <p className="mt-4 text-base leading-7 text-[#0b2a4a]/88">{item.solution}</p>
                <Link to={`/${language}/register`} className="mt-8 inline-block">
                  <Button variant="outline" className="border-[#0b2a4a]/14">
                    {t.cta}
                  </Button>
                </Link>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
